import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { Building2, Globe, CreditCard, Search, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

export const AdminTenantsView: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadTenants = async () => {
    setLoading(true);
    try {
      const data = await adminApiService.getTenants();
      if (data.success) {
        setTenants(data.tenants || []);
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const filtered = tenants.filter(
    (t) =>
      t.companyName?.toLowerCase().includes(search.toLowerCase()) ||
      t.userName?.toLowerCase().includes(search.toLowerCase()) ||
      t.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
      t.tenantId?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            <span>Şirket & Tenant Yönetimi</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Platformdaki tüm kiracı (tenant) şirketleri, kullanıcılarını, sitelerini ve abonelik durumlarını denetleyin.
          </p>
        </div>
        <button
          onClick={loadTenants}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Şirket adı, kullanıcı, e-posta veya Tenant ID ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Şirket / Tenant</th>
                <th className="py-3 px-4">Yetkili Kullanıcı</th>
                <th className="py-3 px-4 text-center">Web Siteleri</th>
                <th className="py-3 px-4">Aktif Paketler</th>
                <th className="py-3 px-4">Ödeme Durumu</th>
                <th className="py-3 px-4">Lisans & Altyapı</th>
                <th className="py-3 px-4">Kayıt Tarihi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-indigo-500" />
                    Şirket verileri yükleniyor...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    Kayıtlı tenant bulunamadı.
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.tenantId} className="hover:bg-slate-50/70 transition">
                    <td className="py-3.5 px-4 font-semibold text-slate-900">
                      <div>{t.companyName}</div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {t.tenantId}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="text-slate-800 font-medium">{t.userName}</div>
                      <div className="text-[11px] text-slate-400">{t.userEmail}</div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs">
                        {t.websiteCount}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-700 font-medium">
                      {t.activePlans}
                    </td>
                    <td className="py-3.5 px-4">
                      {t.paymentStatus === 'PAID' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                          <CheckCircle2 className="w-3 h-3" />
                          Ödendi
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                          Ödeme Yok
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="text-[11px] text-slate-600 font-mono">
                        {t.licenseStatus === 'ACTIVE' ? (
                          <span className="text-emerald-600 font-bold">Aktif Lisans</span>
                        ) : t.licenseStatus === 'PENDING_APPROVAL' ? (
                          <span className="text-amber-600 font-bold">Onay Bekliyor</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px]">
                      {t.createdAtFormatted}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
